import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getParseQualityEvents,
  getParseQualitySummary,
  ParseQualityEvent,
  ParseQualityEventKind,
  ParseQualityEventsResponse,
  ParseQualityFeatureRow,
  ParseQualityFeatureTitle,
  ParseQualitySummary,
  ParseQualityWindow,
} from '../api/client'
import { ownerOf } from '../utils/checkinFeatures'
import { BoolParams, boolLabelFor, isBoolList, isBoolSub } from '../utils/boolFeature'

// Admin parse-quality dashboard (parse-edit data collection Stage 7).
//
// V1 is deliberately tables and stat rows, not charts: the point is browsing
// concrete failure cases (checkin body, what the parser produced, what the
// user replaced it with, feedback notes) and watching the weekly
// edited-parse rate move.

const WINDOWS: { value: ParseQualityWindow; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
]

const EVENT_TABS: { value: ParseQualityEventKind; label: string }[] = [
  { value: 'edits', label: 'Edits' },
  { value: 'questions', label: 'Genie questions' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'deletions', label: 'Deleted items' },
]

const fmt = (n: number) => n.toLocaleString()

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toFixed(1)}%`

const formatTime = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatBucket = (bucket: string, granularity: 'day' | 'week') => {
  const d = new Date(bucket.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return bucket
  const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return granularity === 'week' ? `wk of ${label}` : label
}

const userLabel = (e: { user_id: number | null; name: string | null; email: string | null }) => {
  if (e.user_id == null) return 'Unattributed'
  return e.name || e.email || `User ${e.user_id}`
}

// The bool sub-config (if any) behind a row's key and value halves, so a
// stored 1/0 can print as its label. Scalar and list<bool> keep their labels
// flat in `params`; a map keeps them on each half.
const boolHalves = (
  config: Record<string, any> | null | undefined,
): { key: BoolParams | null; value: BoolParams | null } => {
  if (!config) return { key: null, value: null }
  if (isBoolSub(config)) return { key: null, value: config.params ?? {} }
  if (isBoolList(config)) return { key: null, value: config.params }
  if (config.type === 'map') {
    const k = config.params?.key
    const v = config.params?.value
    return {
      key: isBoolSub(k) ? k.params ?? {} : null,
      value: isBoolSub(v) ? v.params ?? {} : null,
    }
  }
  return { key: null, value: null }
}

// Render the value half of one pre/post feature row compactly. Map rows show
// their key in brackets; reference values fall back to the raw item id. A
// yes/no value shows its label, or its raw number when the config is missing.
const rowValue = (r: ParseQualityFeatureRow, config?: Record<string, any> | null): string => {
  const bools = boolHalves(config)
  const decimal = (value: number, params: BoolParams | null): string => {
    return (params && boolLabelFor(value, params)) ?? String(value)
  }
  const parts: string[] = []
  if (r.key_freeform_value != null) parts.push(`[${r.key_freeform_value}]`)
  else if (r.key_decimal_value != null) parts.push(`[${decimal(r.key_decimal_value, bools.key)}]`)
  else if (r.key_reference_value != null) parts.push(`[item #${r.key_reference_value}]`)
  if (r.freeform_value != null) parts.push(String(r.freeform_value))
  if (r.decimal_value != null) parts.push(decimal(r.decimal_value, bools.value))
  if (r.reference_value != null) parts.push(`item #${r.reference_value}`)
  if (r.latitude != null || r.longitude != null) parts.push(`(${r.latitude}, ${r.longitude})`)
  return parts.length > 0 ? parts.join(' ') : '(empty)'
}

const imageRows = (
  image: ParseQualityEvent['pre_image'],
  side: 'pre' | 'post',
): ParseQualityFeatureRow[] => {
  if (!image) return []
  return image.rows ?? (side === 'pre' ? image.pre_rows : image.post_rows) ?? []
}

const SOURCE_BADGE: Record<string, string> = {
  composer: 'bg-amber-100 text-amber-900',
  genie_tool: 'bg-amber-100 text-amber-900',
  genie_bulk_edit: 'bg-amber-100 text-amber-900',
  other: 'bg-gray-100 text-gray-600',
}

const SourceBadge: React.FC<{ source: string | null | undefined }> = ({ source }) => {
  if (!source) return <span className="text-gray-400">—</span>
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
        SOURCE_BADGE[source] ?? SOURCE_BADGE.other
      }`}
    >
      {source}
    </span>
  )
}

// Checkin id as a deep link into the timeline (CheckinsPage scrolls to and
// highlights ?checkin_id=). Row clicks (expand) shouldn't fire when the
// link is the target, hence stopPropagation.
const CheckinLink: React.FC<{ checkinId: number | null | undefined }> = ({ checkinId }) => {
  if (checkinId == null) return <span className="text-gray-400">—</span>
  return (
    <Link
      to={`/checkins?checkin_id=${checkinId}`}
      onClick={(e) => e.stopPropagation()}
      className="font-mono text-xs text-amber-800 hover:underline whitespace-nowrap"
    >
      #{checkinId}
    </Link>
  )
}

const StatTile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({
  label,
  value,
  sub,
}) => (
  <div>
    <div className="text-gray-500 text-sm">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {sub != null && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
)

// Side-by-side superseded-parser-value vs. user-replacement diff for one edit
// event, grouped per changed feature.
const EditDiff: React.FC<{
  event: ParseQualityEvent
  featureTitles: Record<string, ParseQualityFeatureTitle>
  projectTitles: Record<string, string>
}> = ({ event, featureTitles, projectTitles }) => {
  const preRows = imageRows(event.pre_image, 'pre')
  const postRows = imageRows(event.post_image, 'post')

  const featureIds = useMemo(() => {
    const ids = new Set<number>()
    for (const r of [...preRows, ...postRows]) ids.add(r.feature_id)
    for (const id of event.pre_image?.changed_feature_ids ?? []) ids.add(id)
    for (const id of event.post_image?.changed_feature_ids ?? []) ids.add(id)
    return Array.from(ids).sort((a, b) => a - b)
  }, [event, preRows, postRows])

  const projectIds = useMemo(() => {
    const ids = new Set<number>()
    for (const image of [event.pre_image, event.post_image]) {
      for (const p of image?.projects ?? []) ids.add(p.project_id)
      for (const id of image?.changed_project_ids ?? []) ids.add(id)
    }
    return Array.from(ids).sort((a, b) => a - b)
  }, [event])

  const featureLabel = (id: number) => {
    const f = featureTitles[String(id)]
    return f ? `${f.title} (${f.value_type})` : `Feature #${id}`
  }

  const projectLabel = (id: number) => {
    const title = projectTitles[String(id)]
    return `${title ?? `Project #${id}`} (project tag)`
  }

  return (
    <table className="w-full text-xs border border-gray-200 rounded">
      <thead className="bg-gray-100 text-left text-gray-600">
        <tr>
          <th className="px-3 py-1.5 font-medium w-1/4">Feature</th>
          <th className="px-3 py-1.5 font-medium w-3/8">Parsed value (superseded)</th>
          <th className="px-3 py-1.5 font-medium w-3/8">User's replacement</th>
        </tr>
      </thead>
      <tbody>
        {featureIds.map((id) => {
          const pre = preRows.filter((r) => r.feature_id === id)
          const post = postRows.filter((r) => r.feature_id === id)
          return (
            <tr key={id} className="border-t border-gray-200 align-top">
              <td className="px-3 py-2 text-gray-700">{featureLabel(id)}</td>
              <td className="px-3 py-2">
                {pre.length === 0 ? (
                  <span className="text-gray-400 italic">none</span>
                ) : (
                  pre.map((r, i) => (
                    <div key={i} className="text-rose-800 bg-rose-50 rounded px-1.5 py-0.5 mb-0.5 last:mb-0">
                      {rowValue(r, featureTitles[String(id)]?.config)}
                    </div>
                  ))
                )}
              </td>
              <td className="px-3 py-2">
                {post.length === 0 ? (
                  <span className="text-gray-400 italic">removed</span>
                ) : (
                  post.map((r, i) => (
                    <div key={i} className="text-green-800 bg-green-50 rounded px-1.5 py-0.5 mb-0.5 last:mb-0">
                      {rowValue(r, featureTitles[String(id)]?.config)}
                      {ownerOf(r) !== 'parser' && (
                        <span className="ml-1 text-[10px] text-green-600">
                          ({r.manual_override}{ownerOf(r) === 'assistant' ? ', Genie' : ''})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </td>
            </tr>
          )
        })}
        {projectIds.map((id) => {
          const pre = (event.pre_image?.projects ?? []).find((p) => p.project_id === id)
          const post = (event.post_image?.projects ?? []).find((p) => p.project_id === id)
          return (
            <tr key={`p${id}`} className="border-t border-gray-200 align-top">
              <td className="px-3 py-2 text-gray-700">{projectLabel(id)}</td>
              <td className="px-3 py-2">
                {pre ? (
                  <div className="text-rose-800 bg-rose-50 rounded px-1.5 py-0.5 inline-block">tagged</div>
                ) : (
                  <span className="text-gray-400 italic">none</span>
                )}
              </td>
              <td className="px-3 py-2">
                {!post || post.manual_override === 'delete' ? (
                  <span className="text-gray-400 italic">
                    {post ? 'untagged (delete)' : 'removed'}
                  </span>
                ) : (
                  <div className="text-green-800 bg-green-50 rounded px-1.5 py-0.5 inline-block">
                    tagged
                    {ownerOf(post) !== 'parser' && (
                      <span className="ml-1 text-[10px] text-green-600">({post.manual_override})</span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          )
        })}
        {featureIds.length === 0 && projectIds.length === 0 && (
          <tr>
            <td colSpan={3} className="px-3 py-2 text-gray-400 italic">
              No feature rows in this event's images.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

const BodyBlock: React.FC<{ body: string | null | undefined }> = ({ body }) => (
  <div className="mb-3">
    <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
      Checkin body
    </div>
    {body ? (
      <pre className="text-xs whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-2 max-h-48 overflow-y-auto">
        {body}
      </pre>
    ) : (
      <div className="text-xs text-gray-400 italic">unavailable (checkin deleted, no snapshot)</div>
    )}
  </div>
)

const ParseQualityPage: React.FC = () => {
  const [window, setWindow] = useState<ParseQualityWindow>('30d')
  const [summary, setSummary] = useState<ParseQualitySummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState<string | null>(null)

  const [tab, setTab] = useState<ParseQualityEventKind>('edits')
  const [eventsRes, setEventsRes] = useState<ParseQualityEventsResponse | null>(null)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  // Keyset paging, same idiom as LlmUsagePage's recent-calls table.
  const [pageBefore, setPageBefore] = useState<number | null>(null)
  const [pageStack, setPageStack] = useState<(number | null)[]>([])

  useEffect(() => {
    let cancelled = false
    setSummaryLoading(true)
    setSummaryError(null)
    getParseQualitySummary(window)
      .then((res) => {
        if (!cancelled) setSummary(res)
      })
      .catch((e) => {
        if (!cancelled) setSummaryError(e?.response?.data?.detail || e.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [window])

  const fetchEvents = async (kind: ParseQualityEventKind, beforeId: number | null) => {
    setEventsLoading(true)
    setEventsError(null)
    try {
      const res = await getParseQualityEvents({ kind, limit: 50, beforeId })
      setEventsRes(res)
    } catch (e: any) {
      setEventsError(e?.response?.data?.detail || e.message || 'Failed to load')
    } finally {
      setEventsLoading(false)
    }
  }

  useEffect(() => {
    setExpandedId(null)
    setPageBefore(null)
    setPageStack([])
    fetchEvents(tab, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const goNextPage = () => {
    const next = eventsRes?.next_before_id
    if (next == null) return
    setPageStack((s) => [...s, pageBefore])
    setPageBefore(next)
    setExpandedId(null)
    fetchEvents(tab, next)
  }

  const goPrevPage = () => {
    if (pageStack.length === 0) return
    const prev = pageStack[pageStack.length - 1]
    setPageStack((s) => s.slice(0, -1))
    setPageBefore(prev)
    setExpandedId(null)
    fetchEvents(tab, prev)
  }

  const totals = summary?.totals
  const events = eventsRes?.events ?? []
  const featureTitles = eventsRes?.feature_titles ?? {}
  const projectTitles = eventsRes?.project_titles ?? {}

  const pageBtn = (enabled: boolean) =>
    `px-3 py-1.5 rounded-md text-sm border whitespace-nowrap ${
      enabled
        ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100 cursor-pointer'
        : 'bg-gray-100 text-gray-400 border-gray-200'
    }`

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link to="/admin" className="text-sm text-amber-800 hover:underline">← Admin</Link>
            <h1 className="text-3xl font-bold text-gray-800 mt-1">Parse Quality</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setWindow(w.value)}
                className={`px-3 py-1.5 rounded-md text-sm border whitespace-nowrap cursor-pointer ${
                  window === w.value
                    ? 'bg-action text-plum border-amber-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {summaryError && (
          <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3">
            {summaryError}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Parse outcomes</h2>
          {summaryLoading && !summary ? (
            <div className="text-gray-500">Loading…</div>
          ) : totals ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
              <StatTile label="Parses" value={fmt(totals.parse_jobs)} sub={`${fmt(totals.ok)} ok`} />
              <StatTile
                label="Edited ≤ 7d"
                value={pct(totals.edited_within_7d_pct)}
                sub={`${fmt(totals.edited_within_7d)} parses`}
              />
              <StatTile
                label="Loop exhausted"
                value={fmt(totals.loop_exhausted)}
                sub={pct(totals.parse_jobs ? (totals.loop_exhausted / totals.parse_jobs) * 100 : null)}
              />
              <StatTile
                label="Errors"
                value={fmt(totals.error)}
                sub={pct(totals.parse_jobs ? (totals.error / totals.parse_jobs) * 100 : null)}
              />
              <StatTile
                label="Edit events"
                value={fmt(totals.edit_events)}
                sub={`${fmt(totals.genie_questions)} Genie questions`}
              />
              <StatTile
                label="Feedback"
                value={`▲ ${fmt(totals.feedback.up)} · ▼ ${fmt(totals.feedback.down)}`}
                sub={`${fmt(totals.feedback.with_note)} with notes`}
              />
              <StatTile
                label="Auto-items deleted"
                value={fmt(totals.item_deletions.user_explicit + totals.item_deletions.orphan_gc)}
                sub={`${fmt(totals.item_deletions.user_explicit)} by user · ${fmt(totals.item_deletions.orphan_gc)} GC`}
              />
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Parses over time
                <span className="ml-2 text-sm font-normal text-gray-500">
                  by {summary?.bucket ?? 'day'}
                </span>
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-6 py-2 font-medium">Bucket</th>
                    <th className="px-6 py-2 font-medium text-right">Parses</th>
                    <th className="px-6 py-2 font-medium text-right">Edited ≤ 7d</th>
                    <th className="px-6 py-2 font-medium text-right">Edited %</th>
                    <th className="px-6 py-2 font-medium text-right">Loops</th>
                    <th className="px-6 py-2 font-medium text-right">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {summary?.time_series.map((b) => (
                    <tr key={b.bucket} className="border-t border-gray-100">
                      <td className="px-6 py-2">{formatBucket(b.bucket, summary.bucket)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(b.parses)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(b.edited_within_7d)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">
                        {pct(b.parses ? (b.edited_within_7d / b.parses) * 100 : null)}
                      </td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(b.loop_exhausted)}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{fmt(b.error)}</td>
                    </tr>
                  ))}
                  {summary && summary.time_series.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-6 text-center text-gray-500">
                        No parses in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">By trigger</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-6 py-2 font-medium">Trigger</th>
                      <th className="px-6 py-2 font-medium text-right">Parses</th>
                      <th className="px-6 py-2 font-medium text-right">Edited ≤ 7d</th>
                      <th className="px-6 py-2 font-medium text-right">Loops</th>
                      <th className="px-6 py-2 font-medium text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary?.by_trigger.map((t) => (
                      <tr key={t.trigger} className="border-t border-gray-100">
                        <td className="px-6 py-2 font-mono text-xs">{t.trigger}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(t.parses)}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(t.edited_within_7d)}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(t.loop_exhausted)}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(t.error)}</td>
                      </tr>
                    ))}
                    {summary && summary.by_trigger.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-6 text-center text-gray-500">
                          No parses in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Override rates by value type</h2>
                <p className="text-sm text-gray-500">
                  All-time manual_override baseline over checkin_features; predates the telemetry tables.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-6 py-2 font-medium">Value type</th>
                      <th className="px-6 py-2 font-medium text-right">Rows</th>
                      <th className="px-6 py-2 font-medium text-right">Overridden</th>
                      <th className="px-6 py-2 font-medium text-right">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary?.override_rates_by_feature_type.map((r) => (
                      <tr key={r.value_type} className="border-t border-gray-100">
                        <td className="px-6 py-2 font-mono text-xs">{r.value_type}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(r.total_rows)}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{fmt(r.overridden_rows)}</td>
                        <td className="px-6 py-2 text-right tabular-nums">{pct(r.override_rate)}</td>
                      </tr>
                    ))}
                    {summary && summary.override_rates_by_feature_type.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-6 text-center text-gray-500">
                          No checkin feature rows yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex flex-wrap items-center gap-2">
              {EVENT_TABS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-sm border cursor-pointer ${
                    tab === t.value
                      ? 'bg-action text-plum border-amber-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={goPrevPage}
                disabled={eventsLoading || pageStack.length === 0}
                className={pageBtn(!eventsLoading && pageStack.length > 0)}
              >
                ← Previous
              </button>
              <button
                onClick={goNextPage}
                disabled={eventsLoading || eventsRes?.next_before_id == null}
                className={pageBtn(!eventsLoading && eventsRes?.next_before_id != null)}
              >
                Next →
              </button>
            </div>
          </div>
          <div className="px-6 py-2 text-sm text-gray-500 border-b border-gray-100">
            {tab === 'edits' &&
              'User edits that superseded parser output — expand a row to see the parsed value and its replacement side by side. Not limited by the window above.'}
            {tab === 'questions' && 'Explain-parse questions asked through Genie.'}
            {tab === 'feedback' && 'Thumbs up/down and notes left on parses from the timeline.'}
            {tab === 'deletions' && 'Deleted auto-parsed collection items, from both deletion funnels.'}
          </div>

          {eventsError && (
            <div className="px-6 py-3 text-sm text-rose-700 bg-rose-50 border-b border-rose-200">
              {eventsError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                {tab === 'edits' && (
                  <tr>
                    <th className="px-2 py-2 font-medium w-6"></th>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Checkin</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium">Checkin body</th>
                    <th className="px-3 py-2 font-medium text-right">Changes</th>
                    <th className="px-3 py-2 font-medium text-right">Body edited</th>
                  </tr>
                )}
                {tab === 'questions' && (
                  <tr>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Checkin</th>
                    <th className="px-3 py-2 font-medium">Question</th>
                    <th className="px-3 py-2 font-medium">Checkin body</th>
                  </tr>
                )}
                {tab === 'feedback' && (
                  <tr>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Checkin</th>
                    <th className="px-3 py-2 font-medium">Rating</th>
                    <th className="px-3 py-2 font-medium">Note</th>
                    <th className="px-3 py-2 font-medium">Checkin body</th>
                  </tr>
                )}
                {tab === 'deletions' && (
                  <tr>
                    <th className="px-3 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Checkin</th>
                    <th className="px-3 py-2 font-medium">Collection</th>
                    <th className="px-3 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 font-medium">Deleted by</th>
                    <th className="px-3 py-2 font-medium">GC context</th>
                    <th className="px-3 py-2 font-medium text-right">Refs</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {tab === 'edits' &&
                  events.map((e) => {
                    const isExpanded = expandedId === e.id
                    const changed = new Set<string>([
                      ...(e.pre_image?.changed_feature_ids ?? []).map((id) => `f${id}`),
                      ...(e.post_image?.changed_feature_ids ?? []).map((id) => `f${id}`),
                      ...(e.pre_image?.changed_project_ids ?? []).map((id) => `p${id}`),
                      ...(e.post_image?.changed_project_ids ?? []).map((id) => `p${id}`),
                    ])
                    return (
                      <React.Fragment key={e.id}>
                        <tr
                          onClick={() => setExpandedId(isExpanded ? null : e.id)}
                          className={`border-t border-gray-100 cursor-pointer ${
                            isExpanded ? 'bg-amber-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-2 py-2 text-gray-400 text-center select-none">
                            {isExpanded ? '▾' : '▸'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {formatTime(e.created_at)}
                          </td>
                          <td className="px-3 py-2">{userLabel(e)}</td>
                          <td className="px-3 py-2"><CheckinLink checkinId={e.checkin_id} /></td>
                          <td className="px-3 py-2"><SourceBadge source={e.source} /></td>
                          <td className="px-3 py-2 text-gray-600 max-w-md truncate" title={e.body ?? ''}>
                            {e.body ?? <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{changed.size}</td>
                          <td className="px-3 py-2 text-right">
                            {e.body_changed ? 'yes' : <span className="text-gray-400">no</span>}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-t border-gray-100 bg-gray-50">
                            <td></td>
                            <td colSpan={7} className="px-4 py-4">
                              <BodyBlock body={e.body} />
                              <EditDiff event={e} featureTitles={featureTitles} projectTitles={projectTitles} />
                              <div className="mt-2 text-[11px] text-gray-400">
                                parse job #{e.parse_job_id ?? '—'}
                                {e.parse_trigger && ` (${e.parse_trigger}, ${e.parse_status})`}
                                {e.genie_chat_id != null && ` · Genie chat #${e.genie_chat_id}`}
                                {e.bulk_edit_journal_id != null && ` · journal #${e.bulk_edit_journal_id}`}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}

                {tab === 'questions' &&
                  events.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {formatTime(e.created_at)}
                      </td>
                      <td className="px-3 py-2">{userLabel(e)}</td>
                      <td className="px-3 py-2"><CheckinLink checkinId={e.checkin_id} /></td>
                      <td className="px-3 py-2 text-gray-800 max-w-md">{e.question ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-md truncate" title={e.body ?? ''}>
                        {e.body ?? <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}

                {tab === 'feedback' &&
                  events.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {formatTime(e.created_at)}
                      </td>
                      <td className="px-3 py-2">{userLabel(e)}</td>
                      <td className="px-3 py-2"><CheckinLink checkinId={e.checkin_id} /></td>
                      <td className="px-3 py-2">
                        {e.rating === 'up' ? (
                          <span className="text-green-700">▲ up</span>
                        ) : e.rating === 'down' ? (
                          <span className="text-rose-700">▼ down</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-800 max-w-md">
                        {e.note ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600 max-w-md truncate" title={e.body ?? ''}>
                        {e.body ?? <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}

                {tab === 'deletions' &&
                  events.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {formatTime(e.created_at)}
                      </td>
                      <td className="px-3 py-2">{userLabel(e)}</td>
                      <td className="px-3 py-2"><CheckinLink checkinId={e.checkin_id} /></td>
                      <td className="px-3 py-2 text-gray-600">{e.collection_name ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-800 max-w-md truncate" title={JSON.stringify(e.item_data)}>
                        {String(
                          (e.item_data as any)?.name ??
                            (e.item_data as any)?.title ??
                            JSON.stringify(e.item_data),
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
                            e.deleted_by === 'user_explicit'
                              ? 'bg-rose-100 text-rose-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {e.deleted_by === 'user_explicit' ? 'user' : 'orphan GC'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-xs">
                        {e.gc_context ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {e.reference_count_at_delete ?? '—'}
                      </td>
                    </tr>
                  ))}

                {events.length === 0 && !eventsLoading && (
                  <tr>
                    <td colSpan={8} className="px-6 py-6 text-center text-gray-500">
                      No {EVENT_TABS.find((t) => t.value === tab)?.label.toLowerCase()} recorded yet.
                    </td>
                  </tr>
                )}
                {eventsLoading && events.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-6 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ParseQualityPage
